export * from './client'
export * from './orders'

// 保留舊的 API 以兼容性
import apiClient from './client'
import type {
  ApiResponse,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  Tenant,
  User,
  Driver,
  Customer,
  TenantPriceRule,
  TenantPriceRuleCreateRequest,
} from '@/types'

export const authApi = {
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const response = await apiClient.post<ApiResponse<LoginResponse>>('/auth/login', data)
    return response.data.data!
  },
  selectTenant: async (session_token: string, tenant_id: string): Promise<LoginResponse> => {
    const response = await apiClient.post<ApiResponse<LoginResponse>>('/auth/select-tenant', { session_token, tenant_id })
    return response.data.data!
  },
  refreshToken: async (refreshToken: string): Promise<{ access_token: string }> => {
    const response = await apiClient.post<ApiResponse<{ access_token: string }>>('/auth/refresh', {
      refresh_token: refreshToken,
    })
    return response.data.data!
  },
}

export const tenantApi = {
  list: async (params?: { page?: number; limit?: number; search?: string }): Promise<{ items: Tenant[]; total: number; page: number; limit: number }> => {
    const response = await apiClient.get<ApiResponse<{ items: any[]; total: number; page: number; limit: number }>>('/tenants', { params })
    const data = response.data.data!
    return {
      items: data.items as Tenant[],
      total: data.total,
      page: data.page,
      limit: data.limit,
    }
  },
  get: async (id: string): Promise<Tenant> => {
    const response = await apiClient.get<ApiResponse<Tenant>>(`/tenants/${id}`)
    return response.data.data!
  },
  update: async (id: string, data: Partial<Tenant>): Promise<Tenant> => {
    const response = await apiClient.patch<ApiResponse<Tenant>>(`/tenants/${id}`, data)
    return response.data.data!
  },
  create: async (data: Partial<Tenant>): Promise<Tenant> => {
    const response = await apiClient.post<ApiResponse<Tenant>>('/tenants', data)
    return response.data.data!
  },
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/tenants/${id}`)
  },
  getBookingLink: async (id: string): Promise<string> => {
    const response = await apiClient.get<ApiResponse<{ token: string }>>(`/tenants/${id}/booking-link`)
    return response.data.data!.token
  },
  getPriceRules: async (tenantId: string): Promise<TenantPriceRule[]> => {
    const response = await apiClient.get<TenantPriceRule[]>(`/tenants/${tenantId}/price-rules`)
    return response.data
  },
  createPriceRule: async (tenantId: string, data: TenantPriceRuleCreateRequest): Promise<TenantPriceRule> => {
    const response = await apiClient.post<TenantPriceRule>(`/tenants/${tenantId}/price-rules`, data)
    return response.data
  },
  updatePriceRule: async (tenantId: string, ruleId: string, data: Partial<TenantPriceRuleCreateRequest>): Promise<TenantPriceRule> => {
    const response = await apiClient.patch<TenantPriceRule>(`/tenants/${tenantId}/price-rules/${ruleId}`, data)
    return response.data
  },
  deletePriceRule: async (tenantId: string, ruleId: string): Promise<void> => {
    await apiClient.delete(`/tenants/${tenantId}/price-rules/${ruleId}`)
  },
  importPriceRules: async (tenantId: string, file: File, airports?: string[]): Promise<{ created: number; updated: number; errors: string[] }> => {
    const formData = new FormData()
    formData.append('file', file)
    if (airports?.length) formData.append('airports', airports.join(','))
    const response = await apiClient.post(`/tenants/${tenantId}/price-rules/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },
}

export const userApi = {
  list: async (params?: { page?: number; limit?: number; role?: string; status?: string }): Promise<{ items: User[]; total: number }> => {
    const response = await apiClient.get<ApiResponse<{ items: User[]; total: number }>>('/users', { params })
    return response.data.data!
  },
}

export const driverApi = {
  list: async (params?: { page?: number; limit?: number; status?: string; vehicle_type?: string }): Promise<{ items: Driver[]; total: number }> => {
    const response = await apiClient.get<ApiResponse<{ items: Driver[]; total: number }>>('/drivers', { params })
    return response.data.data!
  },
}

export const customerApi = {
  list: async (params?: { page?: number; limit?: number; search?: string }): Promise<{ items: Customer[]; total: number }> => {
    const response = await apiClient.get<ApiResponse<{ items: Customer[]; total: number }>>('/customers', { params })
    return response.data.data!
  },
}

export { apiClient as default }
